import React from "react";
import {
  Button,
  Card,
  Col,
  Row,
  Badge,
  Spinner,
  Modal,
} from "@themesberg/react-bootstrap";
import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postApi } from "../../../redux/api";
import useQuery from "../../../hooks/useQuery";
import CustomTable from "../../../components/CustomTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faEye,
  faSync,
  faUndo,
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import { Routes } from "../../../routes";
import { formatNumber } from "../../../redux/utils";
import { useSelector } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";

function CashOfficeReviewPayments() {
  const history = useNavigate();
  const query = useQuery();
  const user = useSelector((state) => state.auth.user);
  const highlightBatch = query.get("batch");

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postApi("get_cash_office_batches", {
        form: { query_type: "pending" },
      });
      if (data?.result?.length) {
        setBatches(data.result);
      } else {
        setBatches([]);
      }
    } catch (err) {
      console.error("Error fetching review batches:", err);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const viewDetail = (batch) => {
    setSelectedBatch(batch);
    setShowDetail(true);
  };

  const approveBatch = async (batchNumber) => {
    setProcessing(true);
    try {
      const data = await postApi("update_cash_office_batch", {
        batch_number: batchNumber,
        status: "reviewed",
        userId: user?.username,
      });
      if (data?.success) {
        toast.success(`Batch ${batchNumber} approved for review!`);
        setShowDetail(false);
        setSelectedBatch(null);
        fetchBatches();
      } else {
        toast.error(data?.message || "Failed to approve batch");
      }
    } catch (err) {
      toast.error("Failed to approve batch. Please try again.");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const returnBatch = async (batchNumber) => {
    setProcessing(true);
    try {
      const data = await postApi("update_cash_office_batch", {
        batch_number: batchNumber,
        status: "returned",
        userId: user?.username,
      });
      if (data?.success) {
        toast.success(`Batch ${batchNumber} returned.`);
        setShowDetail(false);
        setSelectedBatch(null);
        fetchBatches();
      } else {
        toast.error(data?.message || "Failed to return batch");
      }
    } catch (err) {
      toast.error("Failed to return batch. Please try again.");
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const batchFields = [
    { title: "Batch No", value: "batch_number" },
    {
      title: "No. of PVs",
      custom: true,
      component: (item) => (
        <span className="text-center d-block">{item.no_of_payments}</span>
      ),
    },
    {
      title: "Total Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.total_amount)}</div>
      ),
    },
    {
      title: "Created Date",
      custom: true,
      component: (item) => (
        <span>{moment(item.created_date).format("DD/MM/YYYY")}</span>
      ),
    },
    {
      title: "Action",
      custom: true,
      component: (item) => (
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => viewDetail(item)}
        >
          <FontAwesomeIcon icon={faEye} className="me-1" />
          View & Review
        </Button>
      ),
    },
  ];

  const pvDetailFields = [
    { title: "PV Code", value: "pv_code" },
    { title: "MDA Name", value: "mda_name" },
    {
      title: "Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    {
      title: "Contractor",
      value: "contractor_name",
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          <h4>
            <FontAwesomeIcon icon={faClipboardList} className="me-2" />
            Review Payments — Cash Office
          </h4>
          {highlightBatch && (
            <Badge bg="warning" className="mt-1">
              Latest batch: {highlightBatch}
            </Badge>
          )}
        </div>
        <Button variant="outline-primary" size="sm" onClick={fetchBatches}>
          <FontAwesomeIcon icon={faSync} className="me-1" />
          Refresh
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading batches for review...</p>
            </div>
          ) : batches.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon icon={faClipboardList} size="3x" className="mb-3" />
              <h5>No batches pending review</h5>
              <p>All batches have been reviewed or no batches have been created yet.</p>
            </div>
          ) : (
            <CustomTable
              fields={batchFields}
              data={batches}
              noSort={false}
              hover={true}
            />
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showDetail}
        onHide={() => { setShowDetail(false); setSelectedBatch(null); }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Batch: {selectedBatch?.batch_number}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBatch && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Batch No:</strong> {selectedBatch.batch_number}
                </Col>
                <Col md={4}>
                  <strong>No. of PVs:</strong> {selectedBatch.no_of_payments}
                </Col>
                <Col md={4}>
                  <strong>Total Amount:</strong> ₦
                  {formatNumber(selectedBatch.total_amount)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <strong>Created:</strong>{" "}
                  {moment(selectedBatch.created_date).format("DD/MM/YYYY")}
                </Col>
                <Col md={4}>
                  <strong>Status:</strong>{" "}
                  <Badge bg="warning">{selectedBatch.status}</Badge>
                </Col>
              </Row>
              <h6 className="mt-4 mb-2">PVs in this batch:</h6>
              <CustomTable
                fields={pvDetailFields}
                data={selectedBatch.pvs || []}
                noSort={false}
                hover={true}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => returnBatch(selectedBatch?.batch_number)}
            disabled={processing}
          >
            {processing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faUndo} className="me-1" />
            )}
            Return Batch
          </Button>
          <Button
            variant="success"
            onClick={() => approveBatch(selectedBatch?.batch_number)}
            disabled={processing}
          >
            {processing ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            )}
            Approve Batch
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CashOfficeReviewPayments;
